import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ImageUpload from "./ImageUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const EMPTY = { name: "", address: "", city: "Huntsville, AL", description: "", hero_image: "", website: "", instagram: "", active: true, abe_owned: false, featured: false, gallery: [] };

export default function AdminVenues() {
  const [venues, setVenues] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/venues").then((r) => setVenues(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (v) => { setForm({ ...EMPTY, ...v }); setEditing(v.id); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/venues/${editing}`, form);
      else await api.post("/admin/venues", form);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this venue?")) return;
    try { await api.delete(`/admin/venues/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const field = "w-full rounded-md bg-[#141417] border border-[#27272A] px-3 py-2.5 text-white outline-none focus:border-[#D4AF37] text-sm";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl uppercase text-white">Venues</h1>
        <button data-testid="add-venue-btn" onClick={openNew} className="btn-gold rounded-md px-4 py-2.5 font-oswald text-sm tracking-wide flex items-center gap-1.5"><Plus size={16} /> ADD VENUE</button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {venues.map((v) => (
          <div key={v.id} className="rounded-xl border border-[#27272A] bg-[#18181B] p-4" data-testid={`venue-row-${v.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold text-white flex items-center gap-1.5">{v.name} {v.abe_owned && <Star size={13} className="text-[#D4AF37]" />}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{v.city}</p>
                <span className={`inline-block mt-2 text-[10px] font-oswald tracking-widest px-2 py-0.5 rounded ${v.abe_owned ? "bg-[#D4AF37] text-black" : "bg-[#27272A] text-zinc-300"}`}>{v.abe_owned ? "ABE OWNED" : "PARTNER"}</span>
              </div>
              <div className="flex gap-1">
                <button data-testid={`edit-venue-${v.id}`} onClick={() => openEdit(v)} className="p-1.5 text-zinc-400 hover:text-white"><Pencil size={15} /></button>
                <button data-testid={`delete-venue-${v.id}`} onClick={() => del(v.id)} className="p-1.5 text-zinc-400 hover:text-red-400"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
        {venues.length === 0 && <p className="text-zinc-500">No venues yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121215] border-[#27272A] text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{editing ? "Edit Venue" : "New Venue"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="venue-name" placeholder="Venue Name" className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input placeholder="Address" className={field} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <input placeholder="City" className={field} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            <textarea placeholder="Description" rows={3} className={field} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <input placeholder="Website URL" className={field} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            <input placeholder="Instagram URL" className={field} value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} />
            <ImageUpload label="Hero Image" value={form.hero_image} onChange={(p) => setForm({ ...form, hero_image: p })} testid="venue-hero-upload" />
            <label className="flex items-center justify-between py-1"><span className="text-sm text-zinc-300">ABE Owned Venue</span><Switch data-testid="venue-abe-owned" checked={form.abe_owned} onCheckedChange={(v) => setForm({ ...form, abe_owned: v })} /></label>
            <label className="flex items-center justify-between py-1"><span className="text-sm text-zinc-300">Featured</span><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /></label>
            <label className="flex items-center justify-between py-1"><span className="text-sm text-zinc-300">Active</span><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /></label>
            <button data-testid="save-venue-btn" onClick={save} className="btn-gold w-full rounded-md py-3 font-oswald tracking-widest">SAVE VENUE</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
